import React, { useState, useCallback, useEffect } from "react";
import { POS_COLORS } from "../data/players";
import { buildRosterSlots } from "../engine/simulation";
import { getTierForRank } from "../data/tiers";
import { getAdpPosRank, parseRankNum } from "../data/adpData";
import theme from "../data/theme";
import { captureDataContributionClicked } from "../utils/onboardingAnalytics";
import {
  captureTrendsAskShown,
  captureTrendsAskAccepted,
  captureTrendsAskDismissed,
  captureTrendsAskPersistFailed,
  captureTrendsDateAdded,
} from "../utils/mockAnalytics";
import { isQbPremiumFormat } from "../utils/leagueFormat";
import { epochFromLocalDate } from "../utils/liveBoard";
import {
  readTrendsAskState,
  trendsAskEligible,
  trendsConfirmedVariant,
  setTrendsAskSubmitted,
  setTrendsAskDraftDate,
  setTrendsDateAskDismissed,
  bumpTrendsAskDismissed,
  getCompletedDraftCount,
} from "../utils/draftMilestones";

// Must match EMAIL_PATTERN in api/email/trends-signup.js. input type="email" accepts
// TLD-less addresses ("a@b") that the server pattern rejects, so validating
// here is what keeps a typo from round-tripping into a 400.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Kept under its original key so an address typed into the old email-this-draft
// card is still prefilled here; the trends ask is now its only reader.
const RESULTS_EMAIL_KEY = "auctionArchitect_resultsEmail";

const DAY_MS = 24 * 60 * 60 * 1000;

// The cropped sample chart on the trends ask.
//
// 🔴 THIS IS THE FOUGHT-OVER CHART, NOT THE AVOID CHART — a 4-row crop of
// Gibbs / Bijan / Chase / Amon-Ra. The avoid list is the surprising material
// and it ships in the EMAIL; putting it on the page spends the reason to open
// the thing being signed up for.
//
// 🔴 REGENERATING IT IS PART OF THE SEND-DAY ROUTINE, not a separate chore. The
// provenance line is rendered INTO the image ("1,318 mock auction drafts ·
// Aug 2026 · $200 budget, 10-14 teams"), so a stale asset is self-evidently
// stale to anyone looking at it rather than quietly wrong. Replace the file in
// public/ and keep these two numbers in step if the crop's pixel size changes.
const TRENDS_SAMPLE_SRC = "/trends-sample.png";
const TRENDS_SAMPLE_ALT =
  "Sample of the trends chart: for Jahmyr Gibbs, Bijan Robinson, Ja'Marr Chase and "
  + "Amon-Ra St. Brown, the share of drafters who left with him against the larger "
  + "share who went after him.";

// "Aug 29", in the DEVICE's zone. epochFromLocalDate builds local noon, so
// formatting locally hands back exactly the day the user clicked — the
// round-trip that lets this line function as a typo check.
function shortDate(ms) {
  try {
    return new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
}

// Clicking anywhere in a date field should open the calendar, not just the tiny
// native icon at its edge.
//
// showPicker throws when it is not called from a user gesture, and is absent
// entirely on older browsers, where calling it throws a TypeError. In BOTH
// cases the native icon still works, so a silent catch is the entire fallback.
// No polyfill and no custom calendar component: the platform picker is free,
// accessible, localised, and already correct on touch.
//
// 🔴 CLICK ONLY, never focus. Opening on focus ambushes a keyboard user tabbing
// through the form, and it fights anyone who would rather type the date.
function openDatePicker(e) {
  try {
    e.currentTarget.showPicker();
  } catch {
    /* no user gesture, or an older browser: the native icon still opens it */
  }
}

// Today as YYYY-MM-DD in the DEVICE's zone, for the date input's `min`. The
// calendar then greys past days out instead of accepting one and failing the
// past-date check after the fact.
//
// Deliberately LOCAL, not Eastern: this is about which days the user can pick
// on their own calendar. The Eastern conversion happens server-side, where it
// belongs. Recomputed per render rather than cached at module load, so a
// session left open across midnight does not keep yesterday's floor.
function todayLocalISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Their own address on their own device — remembered purely to save retyping it
// on a phone. Both directions are guarded: storage can be unavailable or full.
function readSavedEmail() {
  try {
    return localStorage.getItem(RESULTS_EMAIL_KEY) || "";
  } catch {
    return "";
  }
}

function saveEmail(value) {
  try {
    localStorage.setItem(RESULTS_EMAIL_KEY, value);
  } catch {
    /* non-fatal: the signup was already saved */
  }
}

// "I'll send you what the rooms are doing, the day before you draft."
//
// THE DOMINANT CARD on this screen, above the grade. That placement is the
// point: this is the one ask with a deadline attached to it, and a user who
// scrolls past it has no reason to come back before their draft.
//
// TWO FIELDS, AND IT STAYS TWO. No league name, no platform picker, no confirm
// step. Every field added here converts fewer people, and the two that are left
// are the only two a send actually needs: who to mail and when.
//
// THE DATE IS OPTIONAL ON PURPOSE. Losing the address is worse than losing the
// date — an address with no date lands in the endpoint's `unscheduled` bucket
// and is still reachable, while a date with no address is nothing at all. So
// submitting with the date blank is a supported path, not a validation failure,
// and it posts draftAt: null.
//
// Nothing here is gated, modal, blocking, or on a timer. A plain text "No
// thanks" is the whole dismissal affordance.
// Every user-facing string in TrendsSignupAsk, in one place.
//
// 🔴 STANDING COPY RULE FOR THIS OBJECT AND EVERY STRING ADDED TO IT: no em
// dash, ever, and no "--" either. It reads as machine-written, and this
// project's main distribution channel bans AI-generated content outright, so a
// dash here is not a style preference. Use a comma, a period, a colon, or
// parentheses, or restructure the sentence. Hyphens inside compound words
// ("up-to-date") are fine.
//
// `success` is a FUNCTION of the two dates rather than a template assembled at
// the call site, so the whole sentence is legible and editable here instead of
// being spread across JSX. It returns a fragment only so the two dates can be
// emphasised; the words live entirely in this object.
const TRENDS_ASK_COPY = {
  headline: "Gain an edge on your opponents before draft day!",
  sub: "Up-to-date, exclusive insights into who Auction drafters are going after, "
    + "and who they are passing on.",
  imageCaption: "a sample of what goes out the day before your draft",
  emailLabel: "Email address",
  emailPlaceholder: "you@example.com",
  // The date input is type="date", which renders no placeholder of its own, so
  // this is its accessible name. "(optional)" is load-bearing: a blank date is
  // a supported path that stores to the unscheduled bucket, and without the
  // word the copy and the behaviour disagree for someone who has no date yet.
  datePlaceholder: "When's your draft? (optional)",
  submitLabel: "Send me the latest insights before my draft!",
  // Sentence case to match submitLabel: the two share one button and swapping
  // between sentence case and ALL CAPS across its states reads as a glitch.
  sendingLabel: "Saving...",
  dismissLabel: "No thanks",
  note: "One email, timed to your draft. Not a newsletter.",

  // Both dates are named because naming the date back is how someone catches
  // their own typo, and the one they need to check is the DRAFT date, the one
  // they actually typed.
  success: (sendDate, draftDate) => (
    <>You're in! I'll send it <strong style={{ color: theme.colors.text.primary }}>{sendDate}</strong>,
      the day before your <strong style={{ color: theme.colors.text.primary }}>{draftDate}</strong> draft.</>
  ),
  successNoDate: "You're in. Without a date I can't time it to your draft, so I'll send you the next read that goes out.",

  // ── The compact confirmed card, shown once someone has signed up ──
  // Both variants are deliberately much smaller than the ask: no chart, no
  // headline. The first is reassurance and carries no controls at all.
  confirmedDated: (sendDate) => `You're on the list. I'll send it ${sendDate}, the day before your draft.`,
  // The second is the only path back from the endpoint's unscheduled bucket.
  confirmedNeedsDate: "You're on the list. Add your draft date so I know when to send it.",
  dateOnlySubmitLabel: "Add it",

  errBadEmail: "That doesn't look like an email address. Check it and try again.",
  errDateRequired: "Pick your draft date first.",
  errBadDate: "I couldn't read that date. Leave it blank if you don't know yet.",
  errPastDate: "That date's already passed. Is that the right draft day?",
  errRateLimited: "You've signed up a few times today. Try again tomorrow.",
  errGeneric: "Couldn't save that just now. Try again?",
};

export function TrendsSignupAsk({ teams, budget, isSflex, qbSlots, positions }) {
  // Read storage once at mount. Re-reading would let the card pop in or vanish
  // mid-session, and neither the dismiss count nor the draft count can change
  // while a results screen is up. The same snapshot decides whether the FULL
  // ask shows and, when it does not, which compact confirmed card takes its
  // place, so the two can never disagree about what the user has already done.
  const [mountState] = useState(readTrendsAskState);
  const [completedAtMount] = useState(getCompletedDraftCount);
  const eligible = trendsAskEligible(mountState, completedAtMount);
  const [email, setEmail] = useState(readSavedEmail);
  const [date, setDate] = useState("");
  const [state, setState] = useState("ask"); // ask | sending | done | hidden
  const [sentDraftAt, setSentDraftAt] = useState(null);
  const [error, setError] = useState("");
  // A missing or broken asset removes the figure instead of leaving a
  // broken-image icon sitting in the dominant card on the screen.
  const [imgOk, setImgOk] = useState(true);

  // The denominator, fired once when the card actually renders — an impression
  // count that included suppressed screens would make every rate meaningless.
  useEffect(() => {
    if (eligible) captureTrendsAskShown();
  }, [eligible]);

  const dismiss = () => {
    captureTrendsAskDismissed({ dismissCount: bumpTrendsAskDismissed(getCompletedDraftCount()) });
    setState("hidden");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (state === "sending") return;

    // Validated against the SERVER's pattern, not the browser's. type="email"
    // accepts "ben@gmail" — no TLD — which api/email/trends-signup.js rejects
    // with a 400. This codebase has already shipped that bug once; the input
    // type is defence in depth, this line is the actual check.
    const trimmed = email.trim();
    if (!EMAIL_PATTERN.test(trimmed)) {
      setError(TRENDS_ASK_COPY.errBadEmail);
      return;
    }

    // Blank is the supported "don't know yet" path. Non-blank and unparseable
    // is a real error worth surfacing: it only happens on a browser that fell
    // back to a text box, and silently posting null would drop what they typed.
    const raw = date.trim();
    const draftAt = raw ? epochFromLocalDate(raw) : null;
    if (raw && draftAt === null) {
      setError(TRENDS_ASK_COPY.errBadDate);
      return;
    }
    // A past date is a promise that can never fire: the send runs the day
    // before, and that day is gone. Cheaper to catch here than to silently
    // never email someone who thinks they signed up.
    if (draftAt !== null && draftAt < Date.now() - DAY_MS) {
      setError(TRENDS_ASK_COPY.errPastDate);
      return;
    }

    setError("");
    setState("sending");

    let res;
    try {
      res = await fetch("/api/email/trends-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, draftAt, teams, budget, isSflex, qbSlots, positions }),
      });
    } catch {
      // Never swallow a network failure into a fake success. The user is
      // waiting on something to arrive; the card stays up and says to retry.
      setState("ask");
      setError(TRENDS_ASK_COPY.errGeneric);
      return;
    }

    if (!res.ok) {
      setState("ask");
      setError(res.status === 429
        ? TRENDS_ASK_COPY.errRateLimited
        : TRENDS_ASK_COPY.errGeneric);
      return;
    }

    // Only now, after a 200. A done-flag set before the POST resolves is a
    // signup the user believes in and the server never saw — and this one is
    // permanent, so getting it wrong retires the ask for good.
    // The endpoint answers with the YYYY-MM-DD it actually filed this under,
    // computed in US Eastern. That is the only value that agrees with the day
    // the send goes out, so it is what gets persisted rather than a locally
    // derived one. A body that fails to parse simply leaves the date unknown,
    // which routes the user to the date prompt instead of inventing a day.
    const body = await res.json().catch(() => ({}));
    const filedDate = typeof body?.draftDate === "string" ? body.draftDate : null;

    // 🔴 CAPTURED FIRST, ahead of every local write. The accept is a fact about
    // the SERVER's answer; nothing about local bookkeeping is allowed to
    // suppress it. It used to run after the persistence, which put the whole
    // conversion metric downstream of localStorage.
    captureTrendsAskAccepted({
      hasDate: draftAt !== null,
      // Anchored at local noon by epochFromLocalDate, so rounding to whole days
      // is exact instead of off-by-one at either end of the day.
      daysUntilDraft: draftAt === null ? null : Math.round((draftAt - Date.now()) / DAY_MS),
    });

    // 🔴 THE PERSISTENCE GETS ITS OWN GUARD, and a failure here does NOT change
    // what the user sees. Everything from here down used to sit outside the
    // handler's try/catch, which wraps only the fetch, so a throw skipped both
    // the capture above and the success state below: the server would hold a
    // signup while the user stared at the form as though it had failed.
    //
    // Losing the local record costs them the confirmed card on a later visit.
    // Being told their signup failed when it did not is worse, and invites a
    // duplicate submission on top.
    try {
      saveEmail(trimmed);
      setTrendsAskSubmitted({ email: trimmed, draftDate: filedDate });
    } catch (err) {
      // Normalised here rather than at the analytics layer so the event carries
      // a usable value even when what was thrown is not an Error.
      captureTrendsAskPersistFailed({ reason: (err && err.name) || "unknown" });
    }

    setSentDraftAt(draftAt);
    setState("done");
  };

  // Dismissed in this session: nothing, and mountState.submitted is false here
  // by construction, so there is no confirmed card to fall through to either.
  if (state === "hidden") return null;
  // Signed up on an earlier screen. The full ask is gone for good; the compact
  // card takes its place rather than the card silently disappearing.
  if (!eligible) {
    return (
      <TrendsConfirmedCard
        mountState={mountState}
        teams={teams}
        budget={budget}
        isSflex={isSflex}
        qbSlots={qbSlots}
        positions={positions}
      />
    );
  }

  const cardStyle = {
    background: theme.helpers.brandBg(0.08),
    border: `2px solid ${theme.colors.brand.orange}`,
    borderRadius: 12,
    padding: "14px 16px",
    // Past roughly this width the two columns stop filling the row and the
    // card just grows whitespace, so cap it and centre it rather than letting
    // an ultrawide monitor stretch it arbitrarily. Applies to the done state
    // too, since both branches share this object.
    maxWidth: 1100,
    margin: "0 auto 12px",
  };

  if (state === "done") {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 13, color: theme.colors.text.secondary, fontFamily: theme.fonts.body, lineHeight: 1.5 }}>
          {sentDraftAt
            ? TRENDS_ASK_COPY.success(shortDate(sentDraftAt - DAY_MS), shortDate(sentDraftAt))
            : TRENDS_ASK_COPY.successNoDate}
        </div>
      </div>
    );
  }

  const sending = state === "sending";
  const fieldStyle = {
    minWidth: 0,
    boxSizing: "border-box",
    background: theme.colors.bg.raised,
    border: `1px solid ${error ? theme.colors.status.danger : theme.colors.border.default}`,
    borderRadius: 8,
    padding: "9px 12px",
    color: theme.colors.text.primary,
    fontFamily: theme.fonts.body,
    fontSize: 13,
  };

  return (
    <div style={cardStyle}>
      <div style={{
        fontSize: 16, fontWeight: 800, color: theme.colors.text.primary,
        fontFamily: theme.fonts.body, lineHeight: 1.3, marginBottom: 6, maxWidth: 560,
      }}>
        {TRENDS_ASK_COPY.headline}
      </div>
      <div style={{
        fontSize: 12, color: theme.colors.text.muted, fontFamily: theme.fonts.body,
        lineHeight: 1.5, marginBottom: 10, maxWidth: 560,
      }}>
        {TRENDS_ASK_COPY.sub}
      </div>

      {/* Sample chart. A PREVIEW, not a gate — borrow EmailGate's layout
          mechanics, never its language: no blur, no lock, no "unlock", no
          counter. That component withholds a report the visitor came for and
          is right to; this one shows a picture of something that does not
          exist yet, so there is nothing to withhold and a wall would be a lie.

          🔴 NO CSS FADE ON THE BOTTOM EDGE, deliberately. The framing is baked
          into the asset — its fifth row (Puka Nacua) is rendered ghosted, which
          is the "there's more" cue, placed ABOVE the provenance line. A CSS
          bottom fade would wash out "1,318 mock auction drafts · Aug 2026 ·
          $200 budget, 10-14 teams", which is the staleness stamp the whole
          regenerate-on-send-day routine exists to keep legible. The generator
          already puts the fade in the only place it can go without eating that
          line; re-applying it here would undo the point.

          If the asset is ever missing or broken, the figure removes itself
          rather than leaving a broken-image icon in the hero card. */}
      {/* width/height are the INTRINSIC pixels, so the browser reserves the
          right box from the aspect ratio before the bytes land; with
          height:auto that is what stops the card shifting under a thumb already
          reaching for the email field. The file is 2x, so 680x564 stays sharp
          anywhere up to 400 CSS px and shrinks freely below that.
          loading="eager" because this sits above the fold and IS the thing
          being sold; lazy would defer exactly the asset the card exists for. */}

      {/* Chart left, form right. The wrap does all the responsive work: there
          is no media query and no breakpoint constant here on purpose. Two
          columns asking for 340px and 320px plus a 20px gap need about 700px,
          so anything narrower drops the form under the chart and the card
          stacks exactly as it did before, including at 320px. */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
      {imgOk && (
        <figure style={{ margin: 0, flex: "1 1 340px", maxWidth: 400 }}>
          <img
            src={TRENDS_SAMPLE_SRC}
            alt={TRENDS_SAMPLE_ALT}
            width={680}
            height={564}
            loading="eager"
            decoding="async"
            onError={() => setImgOk(false)}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              borderRadius: 8,
            }}
          />
          <figcaption style={{
            marginTop: 6, fontSize: 11, color: theme.colors.text.faint,
            fontFamily: theme.fonts.body, lineHeight: 1.4,
          }}>
            {TRENDS_ASK_COPY.imageCaption}
          </figcaption>
        </figure>
      )}

      {/* RIGHT column: the whole form, plus the note and the dismiss. minWidth 0
          so a long value can never push the column past its share of the row. */}
      <div style={{ flex: "1 1 320px", minWidth: 0 }}>
      <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
          disabled={sending}
          placeholder={TRENDS_ASK_COPY.emailPlaceholder}
          aria-label={TRENDS_ASK_COPY.emailLabel}
          style={{ ...fieldStyle, width: "100%" }}
        />
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); if (error) setError(""); }}
          disabled={sending}
          aria-label={TRENDS_ASK_COPY.datePlaceholder}
          onClick={openDatePicker}
          min={todayLocalISO()}
          style={{ ...fieldStyle, width: "100%" }}
        />
        <button
          type="submit"
          disabled={sending}
          style={{
            width: "100%",
            background: theme.colors.brand.gradientBtn,
            color: "#fff", border: "none",
            padding: "10px 20px",
            fontSize: 12, fontWeight: theme.fontWeight.heavy,
            fontFamily: theme.fonts.display, letterSpacing: "1px",
            borderRadius: 8,
            cursor: sending ? "not-allowed" : "pointer",
            opacity: sending ? 0.55 : 1,
            // The label is a full sentence now, so it MUST be allowed to wrap:
            // nowrap here would push it past the card edge at 320px. lineHeight
            // keeps the wrapped lines from crowding each other, and the button
            // grows in height rather than truncating.
            lineHeight: 1.35,
          }}
        >
          {sending ? TRENDS_ASK_COPY.sendingLabel : TRENDS_ASK_COPY.submitLabel}
        </button>
      </form>

      {error && (
        <div role="alert" style={{
          marginTop: 8, fontSize: 12, color: theme.colors.status.danger,
          fontFamily: theme.fonts.body, lineHeight: 1.4,
        }}>
          {error}
        </div>
      )}

      <div style={{
        marginTop: 8, display: "flex", alignItems: "center", gap: 12,
        flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 11, color: theme.colors.text.faint, fontFamily: theme.fonts.body }}>
          {TRENDS_ASK_COPY.note}
        </span>
        <button
          type="button"
          onClick={dismiss}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            fontSize: 11, color: theme.colors.text.faint,
            fontFamily: theme.fonts.body, textDecoration: "underline",
          }}
        >
          {TRENDS_ASK_COPY.dismissLabel}
        </button>
      </div>
      </div>{/* /right column */}
      </div>{/* /two-column row */}
    </div>
  );
}

// What the trends ask becomes once someone has signed up.
//
// The full ask never returns after a submit, and before this existed the card
// simply vanished. That was fine for a signup that carried a date and a real
// leak for one that did not: those land in the endpoint's
// trends_by_date:unscheduled bucket, which can never be served if the user has
// no way to tell us when they draft. This is that way.
//
// Compact on purpose, both variants: no chart, no headline. The dated variant
// carries NO controls at all, because it is reassurance and not a prompt.
//
// 🔴 THE STALE unscheduled MEMBERSHIP IS LEFT ALONE. Adding a date does not try
// to remove the address from trends_by_date:unscheduled. Stale date-set entries
// are deliberate in that design: the sender re-validates every recipient
// against the canonical record and skips mismatches, and deleting here would
// reintroduce exactly the delete-then-write race that avoids.
function TrendsConfirmedCard({ mountState, teams, budget, isSflex, qbSlots, positions }) {
  const [draftDate, setDraftDate] = useState(mountState.submittedDraftDate);
  const [dismissed, setDismissed] = useState(false);
  const [date, setDate] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // The address the follow-up POST will carry. mountState is the record written
  // at submit time; readSavedEmail is the fallback that makes this work for
  // anyone who signed up BEFORE submittedEmail was persisted, since the submit
  // path has always written that key too.
  const email = (mountState.submittedEmail || readSavedEmail() || "").trim();
  const canPost = EMAIL_PATTERN.test(email);

  // Reuses the same selector the storage layer is tested against, with the
  // in-session changes layered on, so the component cannot invent a fourth
  // state the tests do not know about.
  const variant = trendsConfirmedVariant({
    ...mountState,
    submittedDraftDate: draftDate,
    dateAskDismissed: mountState.dateAskDismissed || dismissed,
  });

  if (variant === "none") return null;
  // Signed up, no date, and no usable address to file one against: storage has
  // been partly cleared. Prompting would produce a request that can only 400,
  // so say nothing rather than offer a control that cannot work.
  if (variant === "needs-date" && !canPost) return null;

  const cardStyle = {
    background: theme.colors.bg.surface,
    border: `1px solid ${theme.colors.border.subtle}`,
    borderRadius: 12,
    padding: "12px 16px",
    maxWidth: 1100,
    margin: "0 auto 12px",
  };
  const lineStyle = {
    fontSize: 13, color: theme.colors.text.secondary,
    fontFamily: theme.fonts.body, lineHeight: 1.5,
  };

  if (variant === "dated") {
    return (
      <div style={cardStyle}>
        <div style={lineStyle}>
          {TRENDS_ASK_COPY.confirmedDated(shortDate(epochFromLocalDate(draftDate) - DAY_MS))}
        </div>
      </div>
    );
  }

  const submitDate = async (e) => {
    e?.preventDefault?.();
    if (sending) return;

    // The date is REQUIRED here, unlike in the full ask. Supplying one is the
    // entire purpose of this variant, so a blank field is an error rather than
    // a supported path.
    const raw = date.trim();
    const draftAt = raw ? epochFromLocalDate(raw) : null;
    if (!raw) { setError(TRENDS_ASK_COPY.errDateRequired); return; }
    if (draftAt === null) { setError(TRENDS_ASK_COPY.errBadDate); return; }
    if (draftAt < Date.now() - DAY_MS) { setError(TRENDS_ASK_COPY.errPastDate); return; }

    setError("");
    setSending(true);

    // The SAME endpoint and the same payload shape as the full ask. No new
    // route and no server change: the canonical record is keyed on
    // sha256(email) and overwrites, so this just fills the date in.
    let res;
    try {
      res = await fetch("/api/email/trends-signup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, draftAt, teams, budget, isSflex, qbSlots, positions }),
      });
    } catch {
      setSending(false);
      setError(TRENDS_ASK_COPY.errGeneric);
      return;
    }

    if (!res.ok) {
      setSending(false);
      setError(res.status === 429 ? TRENDS_ASK_COPY.errRateLimited : TRENDS_ASK_COPY.errGeneric);
      return;
    }

    // Trust the endpoint's own bucket, same as the full ask. If it comes back
    // without one the date was dropped server-side, so stay on the prompt
    // rather than claiming a send day that was never filed.
    const body = await res.json().catch(() => ({}));
    const filedDate = typeof body?.draftDate === "string" ? body.draftDate : null;
    if (!filedDate) {
      setSending(false);
      setError(TRENDS_ASK_COPY.errBadDate);
      return;
    }

    setTrendsAskDraftDate(filedDate);
    captureTrendsDateAdded({
      daysUntilDraft: Math.round((draftAt - Date.now()) / DAY_MS),
    });
    setSending(false);
    setDraftDate(filedDate);
  };

  const dismiss = () => {
    setTrendsDateAskDismissed();
    setDismissed(true);
  };

  const fieldStyle = {
    minWidth: 0,
    boxSizing: "border-box",
    background: theme.colors.bg.raised,
    border: `1px solid ${error ? theme.colors.status.danger : theme.colors.border.default}`,
    borderRadius: 8,
    padding: "8px 10px",
    color: theme.colors.text.primary,
    fontFamily: theme.fonts.body,
    fontSize: 13,
  };

  return (
    <div style={cardStyle}>
      <div style={{ ...lineStyle, marginBottom: 8 }}>
        {TRENDS_ASK_COPY.confirmedNeedsDate}
      </div>

      {/* One row that wraps, so this stays a single compact line on a desktop
          and stacks on a phone without a breakpoint. */}
      <form onSubmit={submitDate} style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <input
          type="date"
          value={date}
          onChange={(e) => { setDate(e.target.value); if (error) setError(""); }}
          disabled={sending}
          aria-label={TRENDS_ASK_COPY.datePlaceholder}
          onClick={openDatePicker}
          min={todayLocalISO()}
          style={{ ...fieldStyle, flex: "0 1 180px" }}
        />
        <button
          type="submit"
          disabled={sending}
          style={{
            flex: "0 0 auto",
            background: theme.colors.brand.gradientBtn,
            color: "#fff", border: "none",
            padding: "9px 18px",
            fontSize: 12, fontWeight: theme.fontWeight.heavy,
            fontFamily: theme.fonts.display, letterSpacing: "1px",
            borderRadius: 8,
            cursor: sending ? "not-allowed" : "pointer",
            opacity: sending ? 0.55 : 1,
            lineHeight: 1.35,
          }}
        >
          {sending ? TRENDS_ASK_COPY.sendingLabel : TRENDS_ASK_COPY.dateOnlySubmitLabel}
        </button>
        <button
          type="button"
          onClick={dismiss}
          disabled={sending}
          style={{
            background: "none", border: "none", padding: 0, cursor: "pointer",
            fontSize: 11, color: theme.colors.text.faint,
            fontFamily: theme.fonts.body, textDecoration: "underline",
          }}
        >
          {TRENDS_ASK_COPY.dismissLabel}
        </button>
      </form>

      {error && (
        <div role="alert" style={{
          marginTop: 8, fontSize: 12, color: theme.colors.status.danger,
          fontFamily: theme.fonts.body, lineHeight: 1.4,
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

function MiniAvatar({ player, size = 24 }) {
  const [err, setErr] = useState(false);
  const sid = player?.sleeper_id;
  const pos = player?.pos || "BN";
  const bg = POS_COLORS[pos]?.bg || "#64748b";
  if (sid && pos !== "DEF" && !err) {
    return <img src={`https://sleepercdn.com/content/nfl/players/${sid}.jpg`} alt="" onError={() => setErr(true)} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: "#0d1320" }} />;
  }
  const parts = (player?.name || "").split(" ");
  const label = pos === "DEF" || pos === "K" ? (player?.team || pos) : (parts.length >= 2 ? parts[0][0] + parts[parts.length - 1][0] : "?").toUpperCase();
  return <div style={{ width: size, height: size, borderRadius: "50%", flexShrink: 0, background: bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: pos === "DEF" || pos === "K" ? 8 : 9, fontWeight: 700, color: "#fff", lineHeight: 1 }}>{label}</div>;
}

const ResultsScreen = React.memo(function ResultsScreen({
  mockDraftLog, mockTeams, mockUserSlot, mockPickNumber,
  resetMockDraft, getPlayerProj, settings,
  onExportText, teamCount, dynamicPrices, userSeatAiDriven, draftId,
}) {
  const [exportCopied, setExportCopied] = useState(false);
  const [tableOpen, setTableOpen] = useState(false);
  const [openAccordions, setOpenAccordions] = useState(new Set());
  const slotDefs = buildRosterSlots(settings.positions);

  // Tier-based value lookup — matches bid engine (TIER_MAPS midpoint × budget).
  // Kept only as the miss fallback for getEstimate.
  const getTierVal = useCallback((player) => {
    const rn = parseRankNum(getAdpPosRank(player.name) || player.posRank);
    const tierObj = getTierForRank(player.pos, rn);
    if (!tierObj) return 1;
    return Math.round(((tierObj.floor + tierObj.ceiling) / 2) * (settings?.budget || 200));
  }, [settings?.budget]);

  // The single user-facing estimate — the same league-dynamic price shown in
  // Team Builder / PlayerCard (keyed `${name}-${pos}`). Over/underpay = paid −
  // estimate is measured against THIS. getTierVal is only a miss fallback.
  const getEstimate = useCallback((player) => (
    dynamicPrices?.get(`${player.name}-${player.pos}`) ?? getTierVal(player)
  ), [dynamicPrices, getTierVal]);

  const handleExport = () => {
    const sorted = [...mockDraftLog].reverse();
    const pad = (s, w) => (s + "").padEnd(w);
    const padL = (s, w) => (s + "").padStart(w);
    const avg = arr => arr.length ? Math.round(arr.reduce((s, l) => s + l.price, 0) / arr.length) : 0;
    const picks1to10 = sorted.filter(l => l.pickNumber <= teamCount);
    const picks11to20 = sorted.filter(l => l.pickNumber >= teamCount + 1 && l.pickNumber <= teamCount * 2);
    const picks21to30 = sorted.filter(l => l.pickNumber >= teamCount * 2 + 1 && l.pickNumber <= teamCount * 3);
    const picks31plus = sorted.filter(l => l.pickNumber >= teamCount * 3 + 1);
    const over50 = sorted.filter(l => l.price >= 50).length;
    const totalSpent = sorted.reduce((s, l) => s + l.price, 0);
    let lines = [];
    lines.push("AUCTION DRAFT RESULTS");
    lines.push("=".repeat(90));
    lines.push(`${sorted.length} picks | ${teamCount} teams | $${totalSpent} total spent`);
    lines.push(`Rd 1 avg: $${avg(picks1to10)} | Rd 2 avg: $${avg(picks11to20)} | Rd 3 avg: $${avg(picks21to30)} | Rd 4+ avg: $${avg(picks31plus)}`);
    lines.push(`$50+ players: ${over50} | $40+ players: ${sorted.filter(l => l.price >= 40).length} | $30+ players: ${sorted.filter(l => l.price >= 30).length}`);
    lines.push("");
    lines.push(pad("Pick", 6) + pad("Pos", 5) + pad("Player", 26) + pad("Team", 6) + pad("Nominated By", 14) + pad("Won By", 14) + padL("Price", 6));
    lines.push("-".repeat(77));
    sorted.forEach(log => {
      lines.push(
        pad("#" + log.pickNumber, 6) +
        pad(log.player.pos, 5) +
        pad(log.player.name.length > 24 ? log.player.name.slice(0, 23) + "." : log.player.name, 26) +
        pad(log.player.team || "", 6) +
        pad(log.nominatorName.length > 12 ? log.nominatorName.slice(0, 11) + "." : log.nominatorName, 14) +
        pad(log.winnerName.length > 12 ? log.winnerName.slice(0, 11) + "." : log.winnerName, 14) +
        padL("$" + log.price, 6)
      );
    });
    lines.push("");
    lines.push("TEAM ROSTERS");
    lines.push("=".repeat(90));
    Array.from({ length: teamCount }, (_, i) => {
      const team = mockTeams[i];
      if (!team) return;
      const totalProj = team.roster.reduce((s, p) => s + getPlayerProj(p.player), 0);
      const posSpend = {};
      team.roster.forEach(p => { posSpend[p.player.pos] = (posSpend[p.player.pos] || 0) + p.price; });
      lines.push("");
      // Opponents always show their archetype; the user's seat shows it only when
      // the AI drove it (Auto-Bid / Quick Sim / Simulate-to-End), tagged as such.
      const showArch = team.profile && (!team.isUser || userSeatAiDriven);
      const archLabel = showArch ? " [" + [
        team.profile.archetype,
        ...(team.profile.modifiers || []).filter(m => !["Balanced QB", "Balanced TE", "Balanced QB SF"].includes(m)).map(m =>
          m === "Homer" && team.profile.favoriteTeam ? `Homer (${team.profile.favoriteTeam})` : m
        ),
      ].join(" · ") + (team.isUser ? " · Auto-Bid profile" : "") + "]" : "";
      lines.push(`${team.name}${team.isUser ? " (You)" : ""}${archLabel} — $${(settings?.budget || 200) - team.budget} spent, $${team.budget} remaining — ${totalProj} proj pts`);
      lines.push(`  Actual: QB $${posSpend.QB || 0} | RB $${posSpend.RB || 0} | WR $${posSpend.WR || 0} | TE $${posSpend.TE || 0} | DEF $${posSpend.DEF || 0} | K $${posSpend.K || 0}`);
      lines.push("  " + "-".repeat(60));
      const filled = {};
      const posQueues = {};
      team.roster.forEach(pick => {
        const pos = pick.player.pos;
        if (!posQueues[pos]) posQueues[pos] = [];
        posQueues[pos].push(pick);
      });
      Object.values(posQueues).forEach(q => q.sort((a, b) => b.price - a.price));
      slotDefs.forEach(slot => {
        if (slot.pos !== "FLEX" && slot.pos !== "BN" && slot.pos !== "SFLEX") {
          const q = posQueues[slot.pos];
          if (q && q.length > 0) filled[slot.id] = q.shift();
        }
      });
      const expFlexOrder = ["RB", "WR", "TE"];
      slotDefs.filter(s => s.pos === "FLEX").forEach(slot => {
        for (const pos of expFlexOrder) {
          if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
        }
      });
      slotDefs.filter(s => s.pos === "SFLEX").forEach(slot => {
        for (const pos of ["QB", "RB", "WR", "TE"]) {
          if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
        }
      });
      slotDefs.filter(s => s.pos === "BN").forEach(slot => {
        for (const pos of ["RB", "WR", "QB", "TE", "DEF", "K"]) {
          if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
        }
      });
      slotDefs.forEach(slot => {
        const pick = filled[slot.id];
        const slotLabel = pad(slot.label.replace(/\d+/, s => s), 5);
        if (pick) {
          lines.push("  " + slotLabel + " " + pad(pick.player.name, 26) + pad(pick.player.pos, 5) + padL("$" + pick.price, 6));
        } else {
          lines.push("  " + slotLabel + " —");
        }
      });
      if (!team.isUser && ((team.myGuys?.length > 0) || (team.sleepers?.length > 0))) {
        lines.push("  " + "-".repeat(60));
        lines.push("  MY GUYS & SLEEPERS");
        (team.myGuys || []).forEach(g => {
          const won = team.roster.some(p => p.player.name === g.name);
          const pick = team.roster.find(p => p.player.name === g.name);
          const logEntry = sorted.find(l => l.player.name === g.name);
          const tierVal = logEntry ? getEstimate(logEntry.player) : "?";
          const status = won ? `Won $${pick.price} / val $${tierVal}` : "Missed";
          lines.push("  " + pad("★ " + g.name, 30) + pad(`+${Math.round((g.boost - 1) * 100)}%`, 6) + status);
        });
        (team.sleepers || []).forEach(g => {
          const won = team.roster.some(p => p.player.name === g.name);
          const pick = team.roster.find(p => p.player.name === g.name);
          const logEntry = sorted.find(l => l.player.name === g.name);
          const tierVal = logEntry ? getEstimate(logEntry.player) : "?";
          const status = won ? `Won $${pick.price} / val $${tierVal}` : "Missed";
          lines.push("  " + pad("◆ " + g.name, 30) + pad(`+${Math.round((g.boost - 1) * 100)}%`, 6) + status);
        });
      }
    });
    lines.push("");
    lines.push("Exported from Mock 1");
    const text = lines.join("\n");
    const onCopied = () => { setExportCopied(true); setTimeout(() => setExportCopied(false), 2000); };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(onCopied).catch(() => {
          const ta = document.createElement("textarea");
          ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
          document.body.appendChild(ta); ta.select();
          try { document.execCommand("copy"); onCopied(); } catch { onExportText(text); }
          document.body.removeChild(ta);
        });
      } else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        try { document.execCommand("copy"); onCopied(); } catch { onExportText(text); }
        document.body.removeChild(ta);
      }
    } catch { onExportText(text); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const buildFilledSlots = (team) => {
    const filled = {};
    const posQueues = {};
    team.roster.forEach(pick => {
      const pos = pick.player.pos;
      if (!posQueues[pos]) posQueues[pos] = [];
      posQueues[pos].push(pick);
    });
    Object.values(posQueues).forEach(q => q.sort((a, b) => b.price - a.price));
    slotDefs.forEach(slot => {
      if (slot.pos !== "FLEX" && slot.pos !== "BN" && slot.pos !== "SFLEX") {
        const q = posQueues[slot.pos];
        if (q && q.length > 0) filled[slot.id] = q.shift();
      }
    });
    const flexOrder = (() => {
      const fp = team.profile?.flexPos;
      if (fp === "WR") return ["WR", "RB", "TE"];
      if (fp === "RB") return ["RB", "WR", "TE"];
      return ["RB", "WR", "TE"];
    })();
    slotDefs.filter(s => s.pos === "FLEX").forEach(slot => {
      for (const pos of flexOrder) {
        if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
      }
    });
    slotDefs.filter(s => s.pos === "SFLEX").forEach(slot => {
      for (const pos of ["QB", "RB", "WR", "TE"]) {
        if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
      }
    });
    slotDefs.filter(s => s.pos === "BN").forEach(slot => {
      for (const pos of ["RB", "WR", "QB", "TE", "DEF", "K"]) {
        if (posQueues[pos] && posQueues[pos].length > 0) { filled[slot.id] = posQueues[pos].shift(); break; }
      }
    });
    return filled;
  };

  // Grade blends value (50%) and finish rank (50%) so a #2 finish feels like a #2 finish
  const calcBlendedGrade = (avgDeltaPct, rank, total) => {
    const valueScore = Math.max(0, Math.min(100, (0.72 - avgDeltaPct) / 0.72 * 100));
    const rankScore = total > 1 ? ((total - rank) / (total - 1)) * 100 : 100;
    const combined = (valueScore + rankScore) / 2;
    return combined >= 75 ? "A" : combined >= 55 ? "B" : combined >= 38 ? "C" : combined >= 22 ? "D" : "F";
  };

  const calcTeamGrade = (team, rank, total) => {
    if (!team) return "—";
    const filled = buildFilledSlots(team);
    const starters = slotDefs.filter(s => s.pos !== "BN").map(s => filled[s.id]).filter(Boolean);
    if (!starters.length) return "—";
    const deltas = starters.map(p => {
      const val = getEstimate(p.player);
      return val > 0 ? (p.price - val) / val : 0;
    });
    const avgDelta = deltas.reduce((s, d) => s + d, 0) / deltas.length;
    return calcBlendedGrade(avgDelta, rank, total);
  };

  const gradeColor = (g) => ({ A: "#4ade80", B: "#a3e635", C: "#facc15", D: "#f97316", F: "#f87171" }[g] || theme.colors.text.muted);
  const gradeDesc = (g) => ({ A: "Elite Value", B: "Solid Draft", C: "Fair Value", D: "Overpaid", F: "Ouch" }[g] || "");

  const SpendBar = ({ posSpend }) => {
    const total = ["QB", "RB", "WR", "TE"].reduce((s, p) => s + (posSpend[p] || 0), 0);
    if (!total) return null;
    return (
      <div style={{ display: "flex", height: 6, borderRadius: 3, overflow: "hidden", gap: 1 }}>
        {["QB", "RB", "WR", "TE"].map(pos => {
          const spend = posSpend[pos] || 0;
          if (!spend) return null;
          return <div key={pos} style={{ flex: spend, background: POS_COLORS[pos]?.bg, opacity: 0.9 }} />;
        })}
      </div>
    );
  };

  const RosterRow = ({ slot, pick }) => {
    const displayPos = (slot.pos === "BN" && pick) ? pick.player.pos : slot.pos;
    const posColor = POS_COLORS[displayPos] || POS_COLORS.QB;
    return (
      <div style={{ display: "flex", alignItems: "center", padding: "4px 12px", gap: 8, borderBottom: "1px solid rgba(255,255,255,0.02)", minHeight: 26 }}>
        <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 9, padding: "1px 5px", minWidth: 34, textAlign: "center" }}>
          {displayPos}
        </span>
        {pick ? (
          <>
            <MiniAvatar player={pick.player} size={24} />
            <span style={{ flex: 1, fontSize: 11, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {pick.player.name}
              {pick.player.team && <span style={{ color: theme.colors.text.faint, marginLeft: 4, fontSize: 10 }}>{pick.player.team}</span>}
            </span>
            <span style={{ fontSize: 11, fontWeight: 700, color: theme.colors.brand.accent, minWidth: 30, textAlign: "right" }}>${pick.price}</span>
            <span style={{ fontSize: 10, color: theme.colors.text.faint, minWidth: 28, textAlign: "right" }}>{getPlayerProj(pick.player)}</span>
          </>
        ) : (
          <span style={{ flex: 1, fontSize: 11, color: "rgba(255,255,255,0.1)", fontStyle: "italic" }}>—</span>
        )}
      </div>
    );
  };

  // ── Computed data ─────────────────────────────────────────────────────────
  const sorted = [...mockDraftLog].reverse();

  const userTeam = mockTeams[mockUserSlot];
  const userFilled = userTeam ? buildFilledSlots(userTeam) : {};
  const starterSlots = slotDefs.filter(s => s.pos !== "BN");
  const benchSlots = slotDefs.filter(s => s.pos === "BN");
  const starterPicks = starterSlots.map(s => userFilled[s.id]).filter(Boolean);
  const userTotalProj = starterPicks.reduce((s, p) => s + getPlayerProj(p.player), 0);

  // Two-pass: sort by proj pts first, then assign rank-aware grades
  const teamRanks = Array.from({ length: teamCount }, (_, i) => {
    const t = mockTeams[i];
    if (!t) return { i, proj: 0, spent: 0, name: `Team ${i + 1}`, isUser: false };
    const tFilled = buildFilledSlots(t);
    const tStarters = slotDefs.filter(s => s.pos !== "BN").map(s => tFilled[s.id]).filter(Boolean);
    const proj = tStarters.reduce((s, p) => s + getPlayerProj(p.player), 0);
    const spent = (settings?.budget || 200) - t.budget;
    return { i, proj, spent, name: t.name, isUser: t.isUser };
  }).sort((a, b) => b.proj - a.proj).map((item, idx) => ({
    ...item,
    rank: idx + 1,
    grade: calcTeamGrade(mockTeams[item.i], idx + 1, teamCount),
  }));

  const finishRank = teamRanks.findIndex(r => r.i === mockUserSlot) + 1;
  const grade = teamRanks.find(r => r.i === mockUserSlot)?.grade || "—";
  const userSpent = userTeam ? (settings?.budget || 200) - userTeam.budget : 0;

  const userPosSpend = {};
  userTeam?.roster.forEach(p => { userPosSpend[p.player.pos] = (userPosSpend[p.player.pos] || 0) + p.price; });
  const starterSpend = starterSlots.reduce((s, slot) => s + (userFilled[slot.id]?.price || 0), 0);
  const benchSpend = userSpent - starterSpend;

  // ── Your Draft Report ─────────────────────────────────────────────────────
  const userRosterWithDelta = (userTeam?.roster || []).map(p => ({
    ...p, tierVal: getEstimate(p.player), delta: p.price - getEstimate(p.player),
  }));
  const yourBestValue = userRosterWithDelta.length
    ? userRosterWithDelta.reduce((best, p) => p.delta < best.delta ? p : best)
    : null;
  const yourBiggestReach = userRosterWithDelta.length
    ? userRosterWithDelta.reduce((big, p) => p.delta > big.delta ? p : big)
    : null;

  const userPicksSorted = [...(userTeam?.roster || [])].sort((a, b) => b.price - a.price);
  const top3Spend = userPicksSorted.slice(0, 3).reduce((s, p) => s + p.price, 0);
  const top3Pct = userSpent > 0 ? top3Spend / userSpent : 0;
  const maxPickPct = userSpent > 0 && userPicksSorted[0] ? userPicksSorted[0].price / userSpent : 0;
  const draftStyle = top3Pct > 0.60 ? "Stars & Scrubs"
    : maxPickPct > 0.30 ? "All-In"
    : maxPickPct < 0.25 && (userTeam?.roster || []).every(p => p.price / userSpent < 0.25) ? "Balanced"
    : "Balanced";
  const draftStyleDesc = {
    "Stars & Scrubs": "Top 3 picks ate 60%+ of your budget. High ceiling, thin bench.",
    "All-In": "One pick dominated your budget. Bet it all on a single star.",
    "Balanced": "No single pick broke the bank. Depth across the board.",
  }[draftStyle];

  // ── Areas of Concern + Strengths ─────────────────────────────────────────
  const CORE_POS = ["QB", "RB", "WR", "TE"];

  // Explicit starter slots per position (not FLEX/SFLEX/BN)
  const starterCountByPos = {};
  slotDefs.filter(s => !["BN", "FLEX", "SFLEX"].includes(s.pos)).forEach(s => {
    starterCountByPos[s.pos] = (starterCountByPos[s.pos] || 0) + 1;
  });

  // User's total picks per position
  const userPicksByPos = {};
  (userTeam?.roster || []).forEach(p => {
    userPicksByPos[p.player.pos] = (userPicksByPos[p.player.pos] || 0) + 1;
  });

  // For each team, their best (most expensive) player per position
  const teamPosBest = Array.from({ length: teamCount }, (_, i) => {
    const t = mockTeams[i];
    if (!t) return {};
    const byPos = {};
    t.roster.forEach(p => {
      if (!byPos[p.player.pos] || p.price > byPos[p.player.pos]) byPos[p.player.pos] = p.price;
    });
    return byPos;
  });

  // User's rank at each position (1 = best, teamCount = worst)
  const userPosRank = {};
  CORE_POS.forEach(pos => {
    const ranked = Array.from({ length: teamCount }, (_, i) => ({ i, price: teamPosBest[i]?.[pos] || 0 }))
      .sort((a, b) => b.price - a.price);
    userPosRank[pos] = ranked.findIndex(r => r.i === mockUserSlot) + 1;
  });

  // Avg delta % per position for user's picks
  const posAvgDelta = {};
  CORE_POS.forEach(pos => {
    const picks = (userTeam?.roster || []).filter(p => p.player.pos === pos);
    if (!picks.length) return;
    const deltas = picks.map(p => { const v = getEstimate(p.player); return v > 0 ? (p.price - v) / v : 0; });
    posAvgDelta[pos] = deltas.reduce((s, d) => s + d, 0) / deltas.length;
  });
  const bestValuePos = CORE_POS.filter(pos => posAvgDelta[pos] !== undefined)
    .sort((a, b) => posAvgDelta[a] - posAvgDelta[b])[0];

  const isSflex = isQbPremiumFormat(settings?.positions);
  const budget = settings?.budget || 200;

  // ── Roster-quality analysis ─────────────────────────────────────────────
  const userRoster = userTeam?.roster || [];

  // Bye week counts among starters
  const starterByes = {};
  starterPicks.forEach(p => {
    const bye = p.player.bye;
    if (bye > 0) { starterByes[bye] = (starterByes[bye] || 0) + 1; }
  });
  const byeStacks = Object.entries(starterByes).filter(([, count]) => count >= 3);
  const maxByeOverlap = Object.values(starterByes).length ? Math.max(...Object.values(starterByes)) : 0;

  // Bench depth per position
  const benchByPos = {};
  CORE_POS.forEach(pos => {
    const total = userPicksByPos[pos] || 0;
    const starters = starterCountByPos[pos] || 0;
    benchByPos[pos] = Math.max(0, total - starters);
  });

  // League-wide position rankings by projection
  const posRankByProj = {};
  CORE_POS.forEach(pos => {
    const all = [];
    Object.values(mockTeams).forEach(t => {
      if (!t?.roster) return;
      t.roster.filter(p => p.player.pos === pos).forEach(p => all.push({ name: p.player.name, proj: getPlayerProj(p.player), isUser: t.isUser }));
    });
    all.sort((a, b) => b.proj - a.proj);
    posRankByProj[pos] = all;
  });

  // FLEX-viable bench
  const flexViableBench = userRoster.filter(p => {
    const pos = p.player.pos;
    if (!["RB", "WR", "TE"].includes(pos)) return false;
    const starters = starterCountByPos[pos] || 0;
    const posPlayers = userRoster.filter(r => r.player.pos === pos).sort((a, b) => getPlayerProj(b.player) - getPlayerProj(a.player));
    const idx = posPlayers.findIndex(r => r.player.name === p.player.name);
    return idx >= starters && getPlayerProj(p.player) > 100;
  });

  // ── Concerns (max 3) ──────────────────────────────────────────────────
  const allConcerns = [];

  // Bye stacks
  byeStacks.forEach(([bye, count]) => {
    allConcerns.push(`${count} starters share Week ${bye} bye.`);
  });

  // Aging RBs in starting lineup
  const agingRBs = starterPicks.filter(p => p.player.pos === "RB" && p.player.age >= 29);
  agingRBs.forEach(p => {
    allConcerns.push(`Aging RB risk — ${p.player.name} is ${p.player.age}.`);
  });

  // Rookie volatility
  const rookieStarters = starterPicks.filter(p => p.player.exp === 0);
  if (rookieStarters.length >= 2) {
    allConcerns.push(`Rookie volatility — ${rookieStarters.length} first-year starters.`);
  }

  // Bottom-3 starter at position
  CORE_POS.forEach(pos => {
    const rankings = posRankByProj[pos] || [];
    const userBest = rankings.find(p => p.isUser);
    if (userBest) {
      const rank = rankings.indexOf(userBest) + 1;
      if (rank > rankings.length - 3 && rankings.length >= 6) {
        allConcerns.push(`${pos}1 projected bottom-3 in the league.`);
      }
    }
  });

  // No bench depth
  CORE_POS.filter(pos => (starterCountByPos[pos] || 0) >= 2).forEach(pos => {
    if (benchByPos[pos] === 0) {
      allConcerns.push(`No ${pos} bench backup.`);
    }
  });

  const concerns = allConcerns.slice(0, 3);

  // ── Strengths (max 3) ─────────────────────────────────────────────────
  const allStrengths = [];

  // Elite positional starter (top-3 at position)
  CORE_POS.forEach(pos => {
    const rankings = posRankByProj[pos] || [];
    const userBest = rankings.find(p => p.isUser);
    if (userBest) {
      const rank = rankings.indexOf(userBest) + 1;
      if (rank <= 3) {
        allStrengths.push(`Elite ${pos} — ${userBest.name} projected #${rank}.`);
      }
    }
  });

  // FLEX depth
  if (flexViableBench.length >= 3) {
    allStrengths.push(`${flexViableBench.length} viable FLEX options on bench — elite depth.`);
  }

  // Clean byes
  if (maxByeOverlap <= 2 && starterPicks.length >= 8) {
    allStrengths.push(`Clean byes — no more than 2 starters share any bye week.`);
  }

  // Young core
  const youngStarters = starterPicks.filter(p => p.player.age && p.player.age <= 25);
  if (youngStarters.length >= 3) {
    allStrengths.push(`Youth movement — ${youngStarters.length} starters 25 or younger.`);
  }

  const strengths = allStrengths.slice(0, 3);

  // ── League superlatives ──────────────────────────────────────────────────
  const superlatives = (() => {
    const teamEntries = Object.entries(mockTeams).filter(([, t]) => t?.roster);

    // Best RB Room — top N RBs by projection (starter count from settings)
    const rbStarterCount = settings?.positions?.RB || 2;
    const rbRooms = teamEntries.map(([id, t]) => {
      const rbs = t.roster.filter(p => p.player.pos === "RB").sort((a, b) => getPlayerProj(b.player) - getPlayerProj(a.player));
      const starters = rbs.slice(0, rbStarterCount);
      const totalProj = starters.reduce((s, p) => s + getPlayerProj(p.player), 0);
      return { id: parseInt(id), name: t.name, isUser: t.isUser, totalProj, names: starters.map(p => p.player.name) };
    }).sort((a, b) => b.totalProj - a.totalProj);
    const bestRB = rbRooms[0] || null;

    // Best WR Corps — top N WRs by projection (starter count from settings)
    const wrStarterCount = settings?.positions?.WR || 3;
    const wrRooms = teamEntries.map(([id, t]) => {
      const wrs = t.roster.filter(p => p.player.pos === "WR").sort((a, b) => getPlayerProj(b.player) - getPlayerProj(a.player));
      const starters = wrs.slice(0, wrStarterCount);
      const totalProj = starters.reduce((s, p) => s + getPlayerProj(p.player), 0);
      return { id: parseInt(id), name: t.name, isUser: t.isUser, totalProj, names: starters.map(p => p.player.name) };
    }).sort((a, b) => b.totalProj - a.totalProj);
    const bestWR = wrRooms[0] || null;

    // Best FLEX — highest-projected player in the actual FLEX roster slot
    const flexSlotIds = slotDefs.filter(s => s.pos === "FLEX").map(s => s.id);
    const flexCandidates = teamEntries.map(([id, t]) => {
      const filled = buildFilledSlots(t);
      let bestPick = null, bestProj = 0;
      flexSlotIds.forEach(slotId => {
        const pick = filled[slotId];
        if (pick) {
          const proj = getPlayerProj(pick.player);
          if (proj > bestProj) { bestProj = proj; bestPick = pick; }
        }
      });
      return { id: parseInt(id), teamName: t.name, isUser: t.isUser, player: bestPick?.player || null, proj: bestProj };
    }).filter(c => c.player).sort((a, b) => b.proj - a.proj);
    const bestFlex = flexCandidates[0] || null;

    return { bestRB, bestWR, bestFlex };
  })();

  const toggleAccordion = (i) => {
    setOpenAccordions(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  return (
    <div style={{ paddingTop: 10 }}>

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <h2 style={{ fontFamily: theme.fonts.display, fontWeight: 900, fontSize: 28,
          background: theme.colors.brand.gradientBtn, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
        }}>DRAFT COMPLETE!</h2>
        <p style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>{mockDraftLog.length} picks • {teamCount} teams</p>
        <button className="btn" onClick={resetMockDraft} style={{
          marginTop: 12, padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
          background: theme.colors.brand.gradientBtn, color: "#000",
        }}>New Mock Draft</button>
      </div>

      {/* Pre-draft trends ask — THE DOMINANT CARD, above the grade on purpose.
          It is the only ask on this screen with a deadline attached, and a user
          who scrolls past it has no reason to return before their draft. It is
          now the ONLY ask on this screen: the email-this-draft card and the
          feedback card were removed once this superseded them.

          🔴 isSflex IS THE LITERAL SLOT TEST, NOT the `isSflex` local defined
          above from isQbPremiumFormat. The local is the DEMAND predicate — it
          answers true for a 2QB roster with no superflex slot — and
          leagueFormat.js says in as many words not to use it where the question
          is "is there an SFLEX slot?". This field is named isSflex and is stored
          under that name by api/email/trends-signup.js, and the project's rule
          after the Aug-24 analytics incident is that a field named is_sflex
          carries the literal test. Passing the local here would file every 2QB
          league as superflex in the signup store, with no way to unmix them
          later.

          qbSlots is what closes that gap: isSflex answers false for a 2QB
          league, so the two fields together are what separate 1QB / 2QB /
          superflex. Sent RAW — the endpoint stores the count and does not
          derive a premium flag from it, because the classifying predicate may
          change and the slot count will not.

          `positions` is the whole roster, sent ALONGSIDE those two rather than
          instead of them: nine integer keys and no PII, and it makes a format
          question nobody has asked yet answerable from rows already captured.
          It also records a `positions: {}` league — a shape the create flow
          treats as valid — as an empty object rather than a null that reads
          identically to "we never asked". */}
      <TrendsSignupAsk
        teams={teamCount}
        budget={budget}
        isSflex={(settings?.positions?.SFLEX || 0) > 0}
        qbSlots={settings?.positions?.QB ?? null}
        positions={settings?.positions ?? null}
      />

      {/* Draft Grade Card */}
      <div style={{ background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`, borderRadius: 12, marginBottom: 12, padding: "18px 20px", display: "flex", alignItems: "center", gap: 24 }}>
        <div style={{ textAlign: "center", minWidth: 64 }}>
          <div style={{ fontFamily: theme.fonts.display, fontSize: 56, fontWeight: 900, lineHeight: 1, color: gradeColor(grade) }}>{grade}</div>
          <div style={{ fontSize: 10, fontWeight: 700, color: gradeColor(grade), marginTop: 2, letterSpacing: 0.5 }}>{gradeDesc(grade)}</div>
        </div>
        <div style={{ width: 1, height: 64, background: "rgba(255,255,255,0.06)" }} />
        <div style={{ display: "flex", gap: 28, flexWrap: "wrap", flex: 1 }}>
          <div>
            <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 2 }}>FINISH RANK</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.primary }}>#{finishRank}<span style={{ fontSize: 11, fontWeight: 400, color: theme.colors.text.muted }}> / {teamCount}</span></div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 2 }}>STARTERS</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.primary }}>${starterSpend}<span style={{ fontSize: 11, fontWeight: 400, color: theme.colors.text.muted }}> / {userSpent}</span></div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 2 }}>BENCH</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.muted }}>${benchSpend}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 2 }}>PROJ PTS</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.brand.accent }}>{userTotalProj}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: theme.colors.text.faint, marginBottom: 2 }}>TOP 3</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: theme.colors.text.primary }}>{Math.round(top3Pct * 100)}%</div>
          </div>
        </div>
      </div>

      {/* League Superlative Cards */}
      {sorted.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "BEST RB ROOM", emoji: "🏈", isUser: superlatives.bestRB?.isUser, headline: superlatives.bestRB?.name, sub: superlatives.bestRB ? `${superlatives.bestRB.names.join(", ")} — ${superlatives.bestRB.totalProj} pts` : "—" },
            { label: "BEST WR CORPS", emoji: "🎯", isUser: superlatives.bestWR?.isUser, headline: superlatives.bestWR?.name, sub: superlatives.bestWR ? `${superlatives.bestWR.names.join(", ")} — ${superlatives.bestWR.totalProj} pts` : "—" },
            { label: "BEST FLEX", emoji: "💪", isUser: superlatives.bestFlex?.isUser, headline: superlatives.bestFlex?.teamName, sub: superlatives.bestFlex ? `${superlatives.bestFlex.player.name} — ${superlatives.bestFlex.proj} pts` : "—" },
          ].map(card => (
            <div key={card.label} style={{ background: card.isUser ? theme.helpers.brandBg(0.08) : theme.colors.bg.surface, border: `1px solid ${card.isUser ? "rgba(255,107,0,0.3)" : theme.colors.border.subtle}`, borderRadius: 10, padding: "12px 14px", position: "relative" }}>
              {card.isUser && <span style={{ position: "absolute", top: 6, right: 8, fontSize: 8, fontWeight: 800, color: "#FF6B00", background: "rgba(255,107,0,0.12)", border: "1px solid rgba(255,107,0,0.25)", borderRadius: 6, padding: "1px 5px", letterSpacing: 0.5 }}>YOU</span>}
              <div style={{ fontSize: 9, fontWeight: 700, color: card.isUser ? "#FF9500" : theme.colors.text.faint, letterSpacing: 0.8, marginBottom: 6 }}>{card.label}</div>
              <div style={{ fontSize: 13, marginBottom: 2 }}>{card.emoji} <span style={{ fontWeight: 700, color: card.isUser ? "#FF6B00" : theme.colors.text.primary }}>{card.headline}</span></div>
              <div style={{ fontSize: 10, color: theme.colors.text.muted, lineHeight: 1.3 }}>{card.sub}</div>
            </div>
          ))}
        </div>
      )}

      {/* Your Draft Report */}
      {userTeam && (
        <div style={{ background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`, borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ padding: "10px 16px", borderBottom: `1px solid ${theme.colors.border.subtle}` }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.secondary, fontFamily: theme.fonts.display }}>YOUR DRAFT REPORT</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 0 }}>
            {/* Best Value */}
            <div style={{ padding: "14px 16px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#4ade80", letterSpacing: 0.8, marginBottom: 8 }}>💎 BEST VALUE</div>
              {yourBestValue ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 4 }}>{yourBestValue.player.name}</div>
                  <div style={{ display: "flex", gap: 10, fontSize: 11, flexWrap: "wrap" }}>
                    <span>Paid <b style={{ color: theme.colors.brand.accent }}>${yourBestValue.price}</b></span>
                    <span style={{ color: theme.colors.text.faint }}>Est <b style={{ color: theme.colors.text.muted }}>${yourBestValue.tierVal}</b></span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: "#4ade80", fontWeight: 600 }}>
                    ${Math.abs(yourBestValue.delta)} under value
                  </div>
                </>
              ) : <span style={{ fontSize: 11, color: theme.colors.text.faint }}>—</span>}
            </div>
            {/* Biggest Reach */}
            <div style={{ padding: "14px 16px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: "#f87171", letterSpacing: 0.8, marginBottom: 8 }}>📈 BIGGEST REACH</div>
              {yourBiggestReach ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.primary, marginBottom: 4 }}>{yourBiggestReach.player.name}</div>
                  <div style={{ display: "flex", gap: 10, fontSize: 11, flexWrap: "wrap" }}>
                    <span>Paid <b style={{ color: theme.colors.brand.accent }}>${yourBiggestReach.price}</b></span>
                    <span style={{ color: theme.colors.text.faint }}>Est <b style={{ color: theme.colors.text.muted }}>${yourBiggestReach.tierVal}</b></span>
                  </div>
                  <div style={{ marginTop: 4, fontSize: 11, color: "#f87171", fontWeight: 600 }}>
                    {yourBiggestReach.tierVal > 0 ? `${Math.round((yourBiggestReach.delta / yourBiggestReach.tierVal) * 100)}% over` : `$${yourBiggestReach.delta} over`}
                  </div>
                </>
              ) : <span style={{ fontSize: 11, color: theme.colors.text.faint }}>—</span>}
            </div>
            {/* Draft Style */}
            <div style={{ padding: "14px 16px" }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, marginBottom: 8 }}>🎯 DRAFT STYLE</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.brand.accent, marginBottom: 4 }}>{draftStyle}</div>
              <div style={{ fontSize: 11, color: theme.colors.text.muted, lineHeight: 1.4 }}>{draftStyleDesc}</div>
              <div style={{ marginTop: 6, fontSize: 10, color: theme.colors.text.faint }}>
                Top 3 picks: ${top3Spend} ({Math.round(top3Pct * 100)}% of budget)
              </div>
            </div>
          </div>
          {/* Concerns + Strengths */}
          {(concerns.length > 0 || strengths.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", borderTop: `1px solid ${theme.colors.border.subtle}` }}>
              <div style={{ padding: "14px 16px", borderRight: "1px solid rgba(255,255,255,0.04)" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#f87171", letterSpacing: 0.8, marginBottom: 10 }}>⚠️ AREAS OF CONCERN</div>
                {concerns.length > 0 ? concerns.map((c, i) => (
                  <div key={i} style={{ fontSize: 11, color: theme.colors.text.muted, lineHeight: 1.5, marginBottom: 6, paddingLeft: 10, borderLeft: "2px solid rgba(248,113,113,0.3)" }}>
                    {c}
                  </div>
                )) : (
                  <div style={{ fontSize: 11, color: theme.colors.text.faint, fontStyle: "italic" }}>No major concerns detected.</div>
                )}
              </div>
              <div style={{ padding: "14px 16px" }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: "#4ade80", letterSpacing: 0.8, marginBottom: 10 }}>✅ STRENGTHS</div>
                {strengths.length > 0 ? strengths.map((s, i) => (
                  <div key={i} style={{ fontSize: 11, color: theme.colors.text.muted, lineHeight: 1.5, marginBottom: 6, paddingLeft: 10, borderLeft: "2px solid rgba(74,222,128,0.3)" }}>
                    {s}
                  </div>
                )) : (
                  <div style={{ fontSize: 11, color: theme.colors.text.faint, fontStyle: "italic" }}>No standout strengths detected.</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* User's Team — full width, 2-column roster */}
      {userTeam && (
        <div style={{ background: theme.helpers.brandBg(0.08), border: `2px solid ${theme.colors.brand.orange}`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontWeight: 800, fontSize: 16, color: theme.colors.brand.orange }}>{userTeam.name}</span>
                <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, color: theme.colors.brand.orange, background: theme.helpers.brandBg(0.15), border: `1px solid ${theme.colors.border.brandLight}`, padding: "2px 6px", borderRadius: 4 }}>YOUR TEAM</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: gradeColor(grade), background: `${gradeColor(grade)}18`, padding: "2px 8px", borderRadius: 4 }}>{grade}</span>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 11, color: theme.colors.text.muted }}>
                <span>${userSpent} spent</span>
                <span style={{ color: theme.colors.brand.accent, fontWeight: 600 }}>{userTotalProj} proj pts</span>
              </div>
            </div>
            <SpendBar posSpend={userPosSpend} />
            <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
              {["QB", "RB", "WR", "TE"].map(pos => userPosSpend[pos] ? (
                <span key={pos} style={{ fontSize: 10, color: theme.colors.text.muted }}>
                  <span style={{ color: POS_COLORS[pos]?.bg, fontWeight: 700 }}>{pos}</span> ${userPosSpend[pos]}
                </span>
              ) : null)}
            </div>
          </div>
          <div className="roster-two-col">
            <div style={{ borderRight: "1px solid rgba(255,255,255,0.04)" }}>
              <div style={{ padding: "6px 12px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>STARTERS</div>
              {starterSlots.map(slot => <RosterRow key={slot.id} slot={slot} pick={userFilled[slot.id]} />)}
            </div>
            <div>
              <div style={{ padding: "6px 12px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>BENCH</div>
              {benchSlots.map(slot => <RosterRow key={slot.id} slot={slot} pick={userFilled[slot.id]} />)}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboard — rows expand to show roster inline */}
      <div style={{ background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
        <div style={{ padding: "10px 16px", borderBottom: `1px solid ${theme.colors.border.subtle}` }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.secondary, fontFamily: theme.fonts.display }}>LEADERBOARD</span>
          <span style={{ fontSize: 10, color: theme.colors.text.faint, marginLeft: 8 }}>click a team to see their roster</span>
        </div>
        {teamRanks.map((r, rank) => {
          const isOpen = !r.isUser && openAccordions.has(r.i);
          const team = mockTeams[r.i];
          const filled = isOpen && team ? buildFilledSlots(team) : null;
          const posSpend = {};
          if (filled && team) team.roster.forEach(p => { posSpend[p.player.pos] = (posSpend[p.player.pos] || 0) + p.price; });
          return (
            <div key={r.i} style={{ borderBottom: "1px solid rgba(255,255,255,0.02)" }}>
              {r.isUser ? (
                <div style={{
                  background: theme.helpers.brandBg(0.08), padding: "8px 16px",
                  display: "flex", alignItems: "center", gap: 12,
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: rank === 0 ? "#facc15" : theme.colors.text.faint, minWidth: 24 }}>#{rank + 1}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: theme.colors.brand.orange }}>{r.name} (You)</div>
                    {userSeatAiDriven && team?.profile && (
                      <div style={{ fontSize: 9, color: theme.colors.text.faint, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {[
                          team.profile.archetype,
                          ...(team.profile.modifiers || []).filter(m => !["Balanced QB", "Balanced TE", "Balanced QB SF"].includes(m)).map(m =>
                            m === "Homer" && team.profile.favoriteTeam ? `Homer (${team.profile.favoriteTeam})` : m
                          ),
                        ].join(" · ")} <span style={{ color: theme.colors.brand.accent, fontWeight: 700 }}>(Auto-Bid profile)</span>
                      </div>
                    )}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: gradeColor(r.grade), background: `${gradeColor(r.grade)}18`, padding: "1px 7px", borderRadius: 4 }}>{r.grade}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.brand.accent, minWidth: 52, textAlign: "right" }}>{r.proj} pts</span>
                  <span style={{ fontSize: 11, color: theme.colors.text.muted, minWidth: 44, textAlign: "right" }}>${r.spent}</span>
                  <span style={{ fontSize: 10, color: theme.colors.text.faint, minWidth: 12 }} />
                </div>
              ) : (
                <button onClick={() => toggleAccordion(r.i)} style={{
                  width: "100%", background: "none", border: "none", cursor: "pointer", padding: "8px 16px",
                  display: "flex", alignItems: "center", gap: 12, textAlign: "left",
                }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: rank === 0 ? "#facc15" : theme.colors.text.faint, minWidth: 24 }}>#{rank + 1}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: theme.colors.text.primary }}>{r.name}</div>
                    {team?.profile && (
                      <div style={{ fontSize: 9, color: theme.colors.text.faint, marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {[
                          team.profile.archetype,
                          ...(team.profile.modifiers || []).filter(m => !["Balanced QB", "Balanced TE", "Balanced QB SF"].includes(m)).map(m =>
                            m === "Homer" && team.profile.favoriteTeam ? `Homer (${team.profile.favoriteTeam})` : m
                          ),
                        ].join(" · ")}
                      </div>
                    )}
                  </span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: gradeColor(r.grade), background: `${gradeColor(r.grade)}18`, padding: "1px 7px", borderRadius: 4, flexShrink: 0 }}>{r.grade}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.brand.accent, minWidth: 52, textAlign: "right", flexShrink: 0 }}>{r.proj} pts</span>
                  <span style={{ fontSize: 11, color: theme.colors.text.muted, minWidth: 44, textAlign: "right", flexShrink: 0 }}>${r.spent}</span>
                  <span style={{ fontSize: 10, color: theme.colors.text.faint, minWidth: 12, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
                </button>
              )}
              {isOpen && filled && team && (
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)", background: "rgba(0,0,0,0.15)" }}>
                  <div style={{ padding: "8px 16px 6px" }}>
                    <SpendBar posSpend={posSpend} />
                    <div style={{ display: "flex", gap: 12, marginTop: 6, flexWrap: "wrap" }}>
                      {["QB", "RB", "WR", "TE"].map(pos => posSpend[pos] ? (
                        <span key={pos} style={{ fontSize: 10, color: theme.colors.text.muted }}>
                          <span style={{ color: POS_COLORS[pos]?.bg, fontWeight: 700 }}>{pos}</span> ${posSpend[pos]}
                        </span>
                      ) : null)}
                    </div>
                  </div>
                  <div className="roster-two-col" style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                    <div style={{ borderRight: "1px solid rgba(255,255,255,0.04)" }}>
                      <div style={{ padding: "5px 12px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>STARTERS</div>
                      {starterSlots.map(slot => <RosterRow key={slot.id} slot={slot} pick={filled[slot.id]} />)}
                    </div>
                    <div>
                      <div style={{ padding: "5px 12px", fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>BENCH</div>
                      {benchSlots.map(slot => <RosterRow key={slot.id} slot={slot} pick={filled[slot.id]} />)}
                    </div>
                  </div>
                  {/* My Guys & Sleepers */}
                  {((team.myGuys?.length > 0) || (team.sleepers?.length > 0)) && (
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.04)", padding: "8px 12px" }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: theme.colors.text.faint, letterSpacing: 0.8, marginBottom: 6 }}>MY GUYS & SLEEPERS</div>
                      {(team.myGuys || []).map(g => {
                        const won = team.roster.some(p => p.player.name === g.name);
                        const pick = team.roster.find(p => p.player.name === g.name);
                        const logEntry = sorted.find(l => l.player.name === g.name);
                        const tierVal = logEntry ? getEstimate(logEntry.player) : "?";
                        return (
                          <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0", fontSize: 11 }}>
                            <span style={{ color: "#eab308", fontSize: 11, flexShrink: 0 }}>★</span>
                            <span style={{ fontWeight: 600, color: won ? theme.colors.text.primary : theme.colors.text.faint, textDecoration: won ? "none" : "line-through", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {g.name}
                            </span>
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#eab308", flexShrink: 0 }}>+{Math.round((g.boost - 1) * 100)}%</span>
                            {won ? (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", flexShrink: 0, minWidth: 62, textAlign: "right" }}>Won ${pick.price} <span style={{ color: theme.colors.text.faint, fontWeight: 400 }}>/ val ${tierVal}</span></span>
                            ) : (
                              <span style={{ fontSize: 10, color: theme.colors.text.faint, flexShrink: 0, minWidth: 62, textAlign: "right" }}>Missed</span>
                            )}
                          </div>
                        );
                      })}
                      {(team.sleepers || []).map(g => {
                        const won = team.roster.some(p => p.player.name === g.name);
                        const pick = team.roster.find(p => p.player.name === g.name);
                        const logEntry = sorted.find(l => l.player.name === g.name);
                        const tierVal = logEntry ? getEstimate(logEntry.player) : "?";
                        return (
                          <div key={g.name} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0", fontSize: 11 }}>
                            <span style={{ color: "#64748b", fontSize: 11, flexShrink: 0 }}>◆</span>
                            <span style={{ fontWeight: 600, color: won ? theme.colors.text.primary : theme.colors.text.faint, textDecoration: won ? "none" : "line-through", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {g.name}
                            </span>
                            <span style={{ fontSize: 9, fontWeight: 700, color: "#64748b", flexShrink: 0 }}>+{Math.round((g.boost - 1) * 100)}%</span>
                            {won ? (
                              <span style={{ fontSize: 10, fontWeight: 700, color: "#4ade80", flexShrink: 0, minWidth: 62, textAlign: "right" }}>Won ${pick.price} <span style={{ color: theme.colors.text.faint, fontWeight: 400 }}>/ val ${tierVal}</span></span>
                            ) : (
                              <span style={{ fontSize: 10, color: theme.colors.text.faint, flexShrink: 0, minWidth: 62, textAlign: "right" }}>Missed</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Full Draft Log — collapsible */}
      <div style={{ background: theme.colors.bg.surface, border: `1px solid ${theme.colors.border.subtle}`, borderRadius: 12, marginBottom: 20, overflow: "hidden" }}>
        <button onClick={() => setTableOpen(o => !o)} style={{
          width: "100%", padding: "12px 16px", background: "none", border: "none", cursor: "pointer",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text.secondary, fontFamily: theme.fonts.display }}>SHOW FULL DRAFT LOG</span>
          <span style={{ fontSize: 11, color: theme.colors.text.faint }}>{tableOpen ? "▲ Hide" : "▼ Show"}</span>
        </button>
        {tableOpen && (
          <>
            <div className="draft-results-row draft-results-header" style={{ display: "flex", padding: "6px 16px", borderTop: `1px solid ${theme.colors.border.subtle}`, borderBottom: `1px solid ${theme.colors.border.subtle}`, fontSize: 10, fontWeight: 700, color: theme.colors.text.faint, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <span className="dr-pick" style={{ width: 36 }}>Pick</span>
              <span className="dr-pos" style={{ width: 34 }}>Pos</span>
              <span className="dr-player" style={{ flex: 1 }}>Player</span>
              <span className="dr-nom" style={{ width: 90 }}>Nominated By</span>
              <span className="dr-won" style={{ width: 90 }}>Won By</span>
              <span className="dr-price" style={{ width: 44, textAlign: "right" }}>Price</span>
            </div>
            <div style={{ maxHeight: 520, overflowY: "auto" }}>
              {sorted.map((log, i) => {
                const posColor = POS_COLORS[log.player.pos];
                const isUser = log.winnerName === mockTeams[mockUserSlot]?.name;
                const isBigSpend = log.price >= 50;
                return (
                  <div key={i} className="draft-results-row" style={{
                    display: "flex", alignItems: "center", padding: "5px 16px", gap: 0,
                    borderBottom: "1px solid rgba(255,255,255,0.02)",
                    background: isUser ? theme.helpers.brandBg(0.04) : isBigSpend ? theme.helpers.brandBg(0.03) : "transparent",
                  }}>
                    <span className="dr-pick" style={{ width: 36, fontSize: 11, color: theme.colors.text.faint, fontWeight: 600 }}>#{log.pickNumber}</span>
                    <span className="dr-pos" style={{ width: 34 }}>
                      <span className="tag" style={{ background: posColor.bg, color: posColor.text, fontSize: 8, padding: "1px 4px", minWidth: 22 }}>{log.player.pos}</span>
                    </span>
                    <span className="dr-player" style={{ flex: 1, fontSize: 11, fontWeight: 500, color: theme.colors.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {log.player.name}<span className="dr-team-tag" style={{ color: theme.colors.text.faint, marginLeft: 4, fontSize: 10 }}>{log.player.team}</span>
                      {(() => {
                        const wTeam = log.winnerId != null ? mockTeams[log.winnerId] : null;
                        if (!wTeam) return null;
                        if (wTeam.myGuys?.some(g => g.name === log.player.name)) return <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "#eab308" }}>(My Guy)</span>;
                        if (wTeam.sleepers?.some(g => g.name === log.player.name)) return <span style={{ marginLeft: 4, fontSize: 9, fontWeight: 700, color: "#94a3b8" }}>(Sleeper)</span>;
                        return null;
                      })()}
                    </span>
                    <span className="dr-nom" style={{ width: 90, fontSize: 10, color: theme.colors.text.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.nominatorName}</span>
                    <span className="dr-won" style={{ width: 90, fontSize: 10, color: isUser ? theme.colors.brand.orange : theme.colors.text.secondary, fontWeight: isUser ? 700 : 400, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{log.winnerName}</span>
                    <span className="dr-price" style={{ width: 44, textAlign: "right", fontSize: 11, fontWeight: 700, color: isBigSpend ? theme.colors.brand.accent : theme.colors.text.secondary }}>${log.price}</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{ display: "flex", gap: 10, justifyContent: "center", paddingBottom: 24 }}>
        <button className="btn" onClick={handleExport} style={{
          padding: "10px 28px", borderRadius: 8, fontSize: 14, fontWeight: 700, fontFamily: "inherit",
          background: exportCopied ? "rgba(74,222,128,0.15)" : theme.helpers.brandBg(0.1),
          color: exportCopied ? "#4ade80" : theme.colors.brand.accent,
          border: `1px solid ${exportCopied ? "rgba(74,222,128,0.3)" : theme.colors.border.brandLight}`,
          cursor: "pointer",
        }}>{exportCopied ? "Copied!" : "Export Results"}</button>
      </div>

      {/* Best moment to ask: they just watched the AI price a room, so they
          know what a real one is worth to it. One muted line, no gate. */}
      <div style={{ textAlign: "center", fontSize: 11, color: theme.colors.text.faint, paddingBottom: 24 }}>
        Got results from your REAL auction?{" "}
        <a
          href="mailto:mock1app@gmail.com?subject=My%20real%20auction%20results"
          onClick={() => captureDataContributionClicked("results")}
          style={{ color: theme.colors.brand.accent, textDecoration: "none" }}
        >Send them over &rarr;</a>
      </div>

    </div>
  );
});

export default ResultsScreen;
